/**
 * Sidebar sections derived from the workspace cache + live state (+ the page tree, for docs items in my folders), and
 * organizing them: collapse (built-in sections on this device, my folders synced), drag-and-drop moves, "Move to
 * folder…", the folder editor (create, rename, emoji or icon), star / mute / leave. Every write is optimistic and
 * queued offline by the DataSource.
 */
import type { NotifyLevel, PageNode } from "@bundle/contracts";
import type { DocsCache } from "../logic/docs.ts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { canCreateChannels, canLeaveChannel } from "../logic/channels.ts";
import { canCloseConversation, isClosed } from "../logic/directMessages.ts";
import { muteNotice, notifyLevelNotice } from "../logic/notifyKinds.ts";
import {
  buildSidebar,
  folderKey,
  folders,
  moveTargets,
  nextFolderRank,
  pagesInFolders,
  planMoveToSection,
  planSidebarMove,
  rankBetween,
  type SectionTarget,
  type SidebarDrop,
  type SidebarSection,
  type SidebarSectionId,
} from "../logic/sidebar.ts";
import { hasUnread, totalBadge, type WorkspaceCache } from "../logic/workspace.ts";
import type { LiveQuery } from "../ports.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

export const FOLDER_NAME_MAX = 80;

/** Icons a folder can show instead of an emoji (names from packages/tokens/icons.json). */
export const FOLDER_ICONS: readonly string[] = ["folder", "star", "board", "users", "code", "bookmark", "inbox", "globe", "sparkles", "pin"];

/** Create / edit a folder (name + optional emoji or icon). */
export type FolderEditor = {
  mode: "create" | "edit";
  folderId?: string;
  name: string;
  emoji?: string;
  icon?: string;
  error?: MessageDescriptor;
  /** Show the error (after a save attempt). */
  showError: boolean;
  /** "Move to folder… → New folder": this row moves into the folder once created. */
  moveRowId?: string;
};

/** "Move to folder…" for one row (a conversation, or a docs item from a folder or the page tree). */
export type MovePicker = { rowId: string; name: string; targets: SectionTarget[] };

/** What a conversation row's context menu offers. */
export type RowActions = { channelId: string; starred: boolean; muted: boolean; notify: NotifyLevel; canLeave: boolean; /** DMs: "Close conversation". */ canClose: boolean };

export type SidebarState = {
  sections: SidebarSection[];
  /** Collapsed section ids (built-in ids or "f:<folderId>"). */
  collapsed: string[];
  /** Unread mentions across the workspace (app badge / tab title). */
  badge: number;
  /** Some conversation has unread messages (the tab title's "* "; logic/workspace hasUnread). */
  unread: boolean;
  /** "Create channel" is offered (`can.createChannels`). */
  canCreateChannels: boolean;
  /** Docs items in my folders (the page tree leaves them out). */
  pagesInFolders: string[];
  editor?: FolderEditor;
  moving?: MovePicker;
  /** A write the server refused (e.g. leaving #general), shown as a toast. */
  notice?: MessageDescriptor;
};

/** `folders: false` (feature off): my folders and manual order are ignored, the sidebar is the plain built-in one. */
export type SidebarOptions = { folders: boolean };

const COLLAPSED_KEY = "bundle.sidebar.collapsed";
const BUILT_IN: SidebarSectionId[] = ["starred", "channels", "dms", "agents"];
const NO_PAGES: Record<string, PageNode> = {};

function nameError(name: string): MessageDescriptor | undefined {
  const n = name.trim();
  if (n === "") return msg("sidebar.folder.nameRequired");
  if (n.length > FOLDER_NAME_MAX) return msg("sidebar.folder.nameTooLong", { max: FOLDER_NAME_MAX });
  return undefined;
}

export class SidebarViewModel extends ViewModel<SidebarState> {
  private readonly ws: WorkspaceViewModel;
  private readonly tree: LiveQuery<DocsCache> | undefined;
  private lastCache: unknown;
  private lastLive: unknown;
  private lastPages: unknown;
  /** Do not disturb boundaries (WorkspaceState.dndAt): moons on DM rows follow the clock. */
  private lastDndAt: number | undefined;
  /** Status expiries (WorkspaceState.statusAt): a DM row's status emoji goes when it runs out. */
  private lastStatusAt: number | undefined;
  private local: SidebarSectionId[];
  private readonly options: SidebarOptions;
  /** Conversations open in a tab or pane, or on screen (always listed in Direct messages). */
  private open: string[] = [];

  constructor(ws: WorkspaceViewModel, options: SidebarOptions = { folders: true }) {
    const local = SidebarViewModel.readCollapsed(ws);
    super({ sections: [], collapsed: local, badge: 0, unread: false, canCreateChannels: false, pagesInFolders: [] });
    this.ws = ws;
    this.local = local;
    this.options = options;
    this.tree = ws.deps.data.pageTree(ws.state.wsId);
    const tree = this.tree;
    this.onDispose(ws.subscribe(() => this.recompute()));
    this.onDispose(tree.subscribe(() => this.recompute()));
    this.onDispose(() => tree.dispose());
    this.recompute();
  }

  private static plainOf = new WeakMap<object, WorkspaceCache>();

  /** The cache without my folders and placements (memoized, so unchanged caches compare equal). */
  private static plain(cache: WorkspaceCache | undefined): WorkspaceCache | undefined {
    if (cache === undefined || (cache.sidebarFolders === undefined && cache.sidebarItems === undefined)) return cache;
    const hit = SidebarViewModel.plainOf.get(cache);
    if (hit !== undefined) return hit;
    const next: WorkspaceCache = { ...cache };
    delete next.sidebarFolders;
    delete next.sidebarItems;
    SidebarViewModel.plainOf.set(cache, next);
    return next;
  }

  private static readCollapsed(ws: WorkspaceViewModel): SidebarSectionId[] {
    const raw = ws.deps.storage.get(COLLAPSED_KEY);
    if (raw === undefined) return [];
    try {
      const v: unknown = JSON.parse(raw);
      return Array.isArray(v) ? BUILT_IN.filter((id) => v.includes(id)) : [];
    } catch {
      return [];
    }
  }

  private get wsId(): string {
    return this.ws.state.wsId;
  }

  private pages(): Record<string, PageNode> {
    return this.tree?.getSnapshot().data?.pages ?? NO_PAGES;
  }

  private collapsedIds(): string[] {
    const cache = this.ws.state.cache;
    const custom = cache === undefined ? [] : folders(cache).filter((f) => f.collapsed).map((f) => folderKey(f.id));
    return [...this.local, ...custom];
  }

  private recompute(force = false): void {
    const { live, dndAt, statusAt } = this.ws.state;
    const cache = this.options.folders ? this.ws.state.cache : SidebarViewModel.plain(this.ws.state.cache);
    const pages = this.pages();
    if (cache === undefined || (!force && cache === this.lastCache && live === this.lastLive && pages === this.lastPages && dndAt === this.lastDndAt && statusAt === this.lastStatusAt)) return;
    this.lastCache = cache;
    this.lastDndAt = dndAt;
    this.lastStatusAt = statusAt;
    this.lastLive = live;
    this.lastPages = pages;
    const sections = buildSidebar(cache, live, pages, this.ws.deps.clock.now(), { open: this.open });
    const inFolders = pagesInFolders(cache);
    const patch: Partial<SidebarState> = {
      sections,
      badge: totalBadge(cache),
      unread: hasUnread(cache),
      collapsed: this.collapsedIds(),
      canCreateChannels: canCreateChannels(cache, this.ws.deps.clock.now()),
      pagesInFolders: inFolders.join() === this.state.pagesInFolders.join() ? this.state.pagesInFolders : inFolders,
    };
    const moving = this.state.moving;
    if (moving !== undefined) {
      const targets = moveTargets(cache, sections, moving.rowId);
      patch.moving = targets.length === 0 ? undefined : { ...moving, targets };
    }
    this.update(patch);
  }

  // ---------- Collapse ----------

  toggleSection(id: string): void {
    const cache = this.ws.state.cache;
    if (id.startsWith("f:")) {
      const f = cache?.sidebarFolders?.[id.slice(2)];
      if (f !== undefined) this.ws.deps.data.updateSidebarFolder(this.wsId, f.id, { collapsed: !f.collapsed });
      return;
    }
    const builtIn = BUILT_IN.find((x) => x === id);
    if (builtIn === undefined) return;
    this.local = this.local.includes(builtIn) ? this.local.filter((x) => x !== builtIn) : [...this.local, builtIn];
    this.ws.deps.storage.set(COLLAPSED_KEY, JSON.stringify(this.local));
    this.update({ collapsed: this.collapsedIds() });
  }

  // ---------- Moving items ----------

  /** A drag ended over a section (`toIndex` = the row's position there after the drop). */
  drop(d: SidebarDrop): void {
    const cache = this.ws.state.cache;
    if (cache === undefined) return;
    const moves = planSidebarMove(this.state.sections, cache, d);
    if (moves.length > 0) this.ws.deps.data.moveSidebarItems(this.wsId, moves);
  }

  /** Opens "Move to folder…" for a row ("p:<pageId>" for a docs item from the page tree). */
  openMove(rowId: string): void {
    const cache = this.ws.state.cache;
    if (cache === undefined) return;
    const targets = moveTargets(cache, this.state.sections, rowId);
    const row = this.state.sections.flatMap((s) => s.rows).find((r) => r.id === rowId);
    const name = row !== undefined ? row.name : rowId.startsWith("p:") ? this.pages()[rowId.slice(2)]?.title ?? "" : "";
    this.update({ moving: { rowId, name, targets } });
  }

  closeMove(): void {
    this.update({ moving: undefined });
  }

  /** Moves the picker's row to `toSection` (a SidebarSection id or "docs"), or into a new folder ("new"). */
  moveTo(toSection: string): void {
    const moving = this.state.moving;
    const cache = this.ws.state.cache;
    if (moving === undefined || cache === undefined) return;
    this.update({ moving: undefined });
    if (toSection === "new") {
      this.startCreateFolder(moving.rowId);
      return;
    }
    const moves = planMoveToSection(this.state.sections, cache, moving.rowId, toSection);
    if (moves.length > 0) this.ws.deps.data.moveSidebarItems(this.wsId, moves);
  }

  // ---------- Conversation actions ----------

  rowActions(rowId: string): RowActions | undefined {
    const cache = this.ws.state.cache;
    if (cache === undefined) return undefined;
    for (const section of this.state.sections) {
      const row = section.rows.find((r) => r.id === rowId);
      if (row === undefined || (row.type !== "channel" && row.type !== "dm")) continue;
      const ms = cache.memberships[row.channelId];
      if (ms === undefined) return undefined;
      return {
        channelId: row.channelId,
        starred: ms.starred,
        muted: ms.muted,
        notify: ms.notify,
        canLeave: row.type === "channel" && canLeaveChannel(cache, row.channelId),
        canClose: row.type === "dm" && canCloseConversation(cache, row.channelId),
      };
    }
    return undefined;
  }

  /**
   * The conversations open in tabs or panes (and the one on screen): Direct messages always lists them, and opening one
   * I had closed reopens it (Slack).
   */
  setOpenConversations(channelIds: readonly string[]): void {
    const next = [...new Set(channelIds)].toSorted();
    const cache = this.ws.state.cache;
    if (cache !== undefined) {
      for (const id of next) {
        if (!this.open.includes(id) && isClosed(cache, id)) this.ws.deps.data.updateMembership(this.wsId, id, { closed: false });
      }
    }
    if (next.join() === this.open.join()) return;
    this.open = next;
    this.recompute(true);
  }

  /** "Close conversation" (DMs): out of Direct messages until someone posts in it or I open it again. */
  close(channelId: string): void {
    this.open = this.open.filter((id) => id !== channelId);
    this.ws.deps.data.updateMembership(this.wsId, channelId, { closed: true });
  }

  /** Star / unstar (to Starred, or back to its default section, in default order). */
  setStarred(channelId: string, on: boolean): void {
    this.ws.deps.data.updateMembership(this.wsId, channelId, { starred: on });
  }

  setMuted(channelId: string, on: boolean): void {
    this.ws.deps.data.updateMembership(this.wsId, channelId, { muted: on });
    this.update({ notice: muteNotice(on) });
  }

  /** A conversation's notification level, from its row's long-press menu (phones). */
  setNotify(channelId: string, level: NotifyLevel): void {
    this.ws.deps.data.updateMembership(this.wsId, channelId, { notify: level });
    this.update({ notice: notifyLevelNotice(level) });
  }

  leave(channelId: string): void {
    this.ws.deps.data.leaveChannel(this.wsId, channelId).catch(() => {
      if (!this.isDisposed) this.update({ notice: msg("channels.leave.failed") });
    });
  }

  dismissNotice(): void {
    this.update({ notice: undefined });
  }

  // ---------- My folders ----------

  startCreateFolder(moveRowId?: string): void {
    const editor: FolderEditor = { mode: "create", name: "", showError: false, error: nameError("") };
    if (moveRowId !== undefined) editor.moveRowId = moveRowId;
    this.update({ editor, moving: undefined });
  }

  startEditFolder(folderId: string): void {
    const f = this.ws.state.cache?.sidebarFolders?.[folderId];
    if (f === undefined) return;
    const editor: FolderEditor = { mode: "edit", folderId, name: f.name, showError: false };
    if (f.emoji !== undefined) editor.emoji = f.emoji;
    if (f.icon !== undefined) editor.icon = f.icon;
    this.update({ editor });
  }

  setEditorName(name: string): void {
    const e = this.state.editor;
    if (e === undefined) return;
    const next: FolderEditor = { ...e, name };
    const error = nameError(name);
    if (error === undefined) delete next.error;
    else next.error = error;
    this.update({ editor: next });
  }

  /** An emoji replaces the icon; undefined = no emoji. */
  setEditorEmoji(emoji: string | undefined): void {
    const e = this.state.editor;
    if (e === undefined) return;
    const next: FolderEditor = { ...e };
    if (emoji === undefined || emoji === "") delete next.emoji;
    else {
      next.emoji = emoji;
      delete next.icon;
    }
    this.update({ editor: next });
  }

  /** An icon (FOLDER_ICONS) replaces the emoji. */
  setEditorIcon(icon: string): void {
    const e = this.state.editor;
    if (e === undefined) return;
    const next: FolderEditor = { ...e, icon };
    delete next.emoji;
    this.update({ editor: next });
  }

  cancelEditor(): void {
    this.update({ editor: undefined });
  }

  /** Saves the editor; returns false (and shows why) when the name isn't valid. */
  saveEditor(): boolean {
    const e = this.state.editor;
    const cache = this.ws.state.cache;
    if (e === undefined || cache === undefined) return false;
    if (e.error !== undefined) {
      this.update({ editor: { ...e, showError: true } });
      return false;
    }
    const name = e.name.trim();
    const data = this.ws.deps.data;
    if (e.mode === "edit" && e.folderId !== undefined) {
      data.updateSidebarFolder(this.wsId, e.folderId, { name, emoji: e.emoji ?? "", icon: e.icon ?? "" });
    } else {
      const id = this.ws.deps.ids.next();
      const req: { id: string; name: string; rank: string; emoji?: string; icon?: string } = { id, name, rank: nextFolderRank(cache) };
      if (e.emoji !== undefined) req.emoji = e.emoji;
      if (e.icon !== undefined) req.icon = e.icon;
      data.createSidebarFolder(this.wsId, req);
      if (e.moveRowId !== undefined) {
        const moves = planMoveToSection(this.state.sections, cache, e.moveRowId, folderKey(id));
        if (moves.length > 0) data.moveSidebarItems(this.wsId, moves);
      }
    }
    this.update({ editor: undefined });
    return true;
  }

  /** Moves one of my folders up (-1) or down (+1) among my folders. */
  moveFolder(folderId: string, delta: -1 | 1): void {
    const cache = this.ws.state.cache;
    if (cache === undefined) return;
    const list = folders(cache);
    const i = list.findIndex((f) => f.id === folderId);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= list.length) return;
    const others = list.filter((f) => f.id !== folderId);
    const rank = rankBetween(others[j - 1]?.rank, others[j]?.rank);
    this.ws.deps.data.updateSidebarFolder(this.wsId, folderId, { rank });
  }

  /** Deletes one of my folders; its items go back to their default section or the page tree. */
  deleteFolder(folderId: string): void {
    this.ws.deps.data.deleteSidebarFolder(this.wsId, folderId);
  }
}
